import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { DepositDays } from "@/routes/_authenticated/_financials/payments-received/-components/deposit-days";
import { PaymentRows } from "@/routes/_authenticated/_financials/payments-received/-components/payment-rows";
import { MethodTotals } from "@/routes/_authenticated/_financials/payments-received/-components/method-totals";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useFacilityToday } from "@/hooks/use-facility-today";
import { useFacility } from "@/lib/facility/facility";
import { optionalDay, optionalOneOf, KEEP_PLACE } from "@/lib/search";
import { orpc } from "@/lib/orpc";

type ReceivedSearch = { from?: string; to?: string; basis?: "received" };

export const Route = createFileRoute("/_authenticated/_financials/payments-received/")({
  validateSearch: (search: Record<string, unknown>): ReceivedSearch => ({
    from: optionalDay(search.from),
    to: optionalDay(search.to),
    basis: optionalOneOf(["received"] as const, search.basis),
  }),
  component: PaymentsReceived,
});

type Basis = "cleared" | "received";

const BASIS_META: Record<Basis, { meta: string; dateHeader: string }> = {
  cleared: {
    meta: "Money that has cleared, net of anything refunded. What the bank statement says.",
    dateHeader: "Cleared",
  },
  received: {
    meta: "Money the office took, net of anything refunded. What the deposit slip says.",
    dateHeader: "Taken",
  },
};

/** What actually came in over a window, grouped into the days it was banked. */
function PaymentsReceived() {
  const { facilityId } = useFacility();
  const today = useFacilityToday(facilityId);
  const monthStart = `${today.slice(0, 7)}-01`;
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<ReceivedSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const from = params.from ?? monthStart;
  const to = params.to ?? today;
  const setFrom = (next: string) => patch({ from: next === monthStart ? undefined : next });
  const setTo = (next: string) => patch({ to: next === today ? undefined : next });
  const basis: Basis = params.basis ?? "cleared";
  const setBasis = (next: Basis) => patch({ basis: next === "received" ? next : undefined });

  const received = useQuery(
    orpc.financials.payments.received.queryOptions({ input: { facilityId, from, to, basis } }),
  );

  return (
    <>
      <PageHeader title="Payments received" meta={BASIS_META[basis].meta} />

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="received-from">From</Label>
          <Input
            id="received-from"
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value || monthStart)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="received-to">To</Label>
          <Input
            id="received-to"
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value || today)}
          />
        </div>
        <div className="space-y-1">
          <Label id="received-basis">Dated by</Label>
          {/* Two different questions: what the bank has, and what the office
              took. A facility tying this to its own deposit slips needs the
              second, and until now could only ask for the first. */}
          <ToggleGroup
            type="single"
            value={basis}
            variant="outline"
            aria-labelledby="received-basis"
            onValueChange={(next) => next && setBasis(next as Basis)}
          >
            <ToggleGroupItem value="cleared">Cleared</ToggleGroupItem>
            <ToggleGroupItem value="received">Received</ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      {received.isError ? (
        // A failed read is not a month with no money in it.
        <ReadFailed
          message="Payments could not be loaded."
          error={received.error}
          onRetry={() => void received.refetch()}
          retrying={received.isFetching}
        />
      ) : received.isPending ? (
        <Skeleton className="h-20 w-full" />
      ) : (
        <>
          {/* Gross and refunded beside the net, because "we took $40,000" and
              "we kept $38,500" are two different sentences at a board meeting. */}
          <StatTiles>
            <StatTile
              label="Payments"
              value={received.data?.totals.count ?? 0}
              hint="In this window"
            />
            <StatTile
              label="Received"
              value={received.data?.totals.amount ?? "$0.00"}
              hint="Gross"
            />
            <StatTile
              label="Refunded"
              value={received.data?.totals.refunded ?? "$0.00"}
              hint="Given back"
            />
            <StatTile label="Net" value={received.data?.totals.net ?? "$0.00"} hint="Kept" />
          </StatTiles>

          {received.data && received.data.byMethod.length > 0 ? (
            <section className="space-y-3">
              <h2 className="text-lg font-semibold">By method</h2>
              <MethodTotals rows={received.data.byMethod} />
            </section>
          ) : null}
        </>
      )}

      {received.isError ? null : (
        <>
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Deposit days</h2>
            <DepositDays days={received.data?.days} pending={received.isPending} />
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Payments</h2>
            <PaymentRows
              items={received.data?.items}
              pending={received.isPending}
              dateHeader={BASIS_META[basis].dateHeader}
            />
          </section>
        </>
      )}
    </>
  );
}
